import { useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LogOut, Home, Heart, Camera, Settings } from "lucide-react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import BackgroundAnimations from "@/components/BackgroundAnimations";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function StudentLayout({ children }) {
  const { user, setUser, logout } = useAuth();
  const location = useLocation();
  const fileRef = useRef(null);
  const [pwDialog, setPwDialog] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/profile/avatar", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setUser((prev) => ({ ...prev, avatar_url: data.avatar_url }));
      toast.success("Photo updated!");
    } catch {
      toast.error("Upload failed");
    }
  };

  const avatarUrl = user?.avatar_url ? `${API_BASE}/api/files/${user.avatar_url}` : null;

  return (
    <div className="min-h-screen bg-[#F7F3EF] relative overflow-hidden">
      <BackgroundAnimations />
      <nav className="bg-white/90 backdrop-blur-sm border-b border-[#EADBC8]/50 sticky top-0 z-40 relative">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/kalemash-logo.png" alt="Kalemash" className="w-9 h-9 rounded-xl" />
            <span className="font-heading text-2xl text-[#5C3A2E] font-semibold">Kalemash</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <Link to="/" className={`px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${location.pathname === "/" ? "bg-[#5C3A2E] text-white shadow-[0_2px_8px_rgb(92,58,46,0.2)]" : "text-[#2B2B2B]/60 hover:bg-[#EADBC8]/40"}`}>
              <Home className="w-3.5 h-3.5 inline mr-0.5 sm:mr-1" /><span className="hidden sm:inline">Dashboard</span><span className="sm:hidden">Home</span>
            </Link>
            <Link to="/feed" className={`px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${location.pathname === "/feed" ? "bg-[#5C3A2E] text-white shadow-[0_2px_8px_rgb(92,58,46,0.2)]" : "text-[#2B2B2B]/60 hover:bg-[#EADBC8]/40"}`}>
              <Heart className="w-3.5 h-3.5 inline mr-0.5 sm:mr-1" />Feed
            </Link>
            {/* Avatar with upload */}
            <button
              data-testid="student-avatar-button"
              onClick={() => fileRef.current?.click()}
              className="relative w-8 h-8 rounded-full overflow-hidden bg-[#EADBC8] flex items-center justify-center ml-2 group cursor-pointer ring-2 ring-transparent hover:ring-[#C6A969] transition-all"
              title="Change your photo"
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs font-semibold text-[#5C3A2E]">{user?.name?.[0]?.toUpperCase() || "?"}</span>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <Camera className="w-3.5 h-3.5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" data-testid="student-avatar-upload-input" />
            <span className="text-sm text-[#2B2B2B]/70 font-body hidden sm:block">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={() => setPwDialog(true)} className="text-[#2B2B2B]/40 hover:text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-8 w-8 p-0" title="Settings">
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              data-testid="student-logout-button"
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </nav>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 relative z-[1]">
        {children}
      </main>
      {/* Change Password Dialog */}
      <Dialog open={pwDialog} onOpenChange={setPwDialog}>
        <DialogContent className="bg-white rounded-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#5C3A2E]">Change Password</DialogTitle>
            <DialogDescription className="text-[#2B2B2B]/50 text-sm">Enter your new password</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (min 8 characters)" className="rounded-xl border-[#EADBC8]" />
            <Button onClick={async () => {
              if (newPw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
              setSaving(true);
              try { await api.post("/auth/change-password", { new_password: newPw }); toast.success("Password changed!"); setPwDialog(false); setNewPw(""); }
              catch (e) { toast.error(formatApiError(e)); }
              setSaving(false);
            }} disabled={saving} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
              {saving ? "Saving..." : "Change Password"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
